/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-06-25 10:33:30
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { connect } from 'react-redux';
import React, { Component, useEffect, useState } from 'react';
import {
  Card,
  TextStyle,
  Button,
  ButtonGroup,
  Page,
  Layout,
  Grid,
  SkeletonDisplayText,
  Icon,
  TextField,
  Link,
  Badge,
  Banner
} from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import { DuplicateMinor, CircleTickMajor, ExternalMinor } from '@shopify/polaris-icons';

import { FullscreenBar } from 'src/components/views/common/fullscreenBar';
import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';
import { trackFSEvent } from 'src/integrations/fullStory';
import { getFullAppUrlThemeEditor, getFullAppUrlV2 } from 'src/helpers';
import { ComposedProps } from './placement.type';
import { routes } from 'src/routes';
import { checkUserEnableShowAppVersionSwitching } from 'src/helpers/account-helper';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';
import { getShop } from 'src/helpers';

import EnableBoostCore from 'src/assets/images/recommendation/enable-boost-core-ssr.png';
import EnableISW from 'src/assets/images/recommendation/enable-isw-block.png';
// Done
const Placement = (props: ComposedProps): Component<ComposedProps> => {
  const {
    i18n,
    checkingEnableBoostCore,
    isEnableBoostCore,
    isEnableISWBlock,
    handleCloseFullscreenPage,
    instantSearchBlockDeepLink,
    enableAppVersionSwitching,
    isEnableShopifyIntegration,
    saving,
    themeId,
    widget,
    embedDeepLink,
    templateId,
    themeName
  } = props;

  const placement = _.get(widget, 'placement', ['search-result-page', 'instant-search-widget']);
  const [showWarning, setWarning] = useState(true);

  const oldStore = enableAppVersionSwitching && isEnableShopifyIntegration;

  const copyClipBoard = (copyText: string) => {
    try {
      const inputText = document.createElement('textarea');
      inputText.value = copyText;
      document.body.appendChild(inputText);
      inputText.select();

      document.execCommand('copy');
      document.body.removeChild(inputText);
      showSABToast(i18n.translate('AdminHTML.Common.Action.copied', { t: copyText }), false);
    } catch (error) {}
  };

  // Enable boost score
  const enableBoostScore = () => {
    if (!_.isEmpty(embedDeepLink)) {
      window.open(embedDeepLink, '_blank', 'noopener,noreferrer');

      // Track Full Story event
      trackFSEvent('Enable Boost Core', {
        theme: themeName
      });
    }
  };

  // Add app block
  const enableInstantSearchBlock = () => {
    if (!_.isEmpty(instantSearchBlockDeepLink)) {
      window.open(instantSearchBlockDeepLink, '_blank', 'noopener,noreferrer');
      // Track Full Story event
      trackFSEvent('Add Instant Search Widget Block', {
        theme: themeName
      });
    }
  };

  // Handle setup action: complete
  const handleCompleteSetup = async () => {
    handleCloseFullscreenPage();
  };

  const goToThemeEditor = () => {
    const url = getFullAppUrlThemeEditor(themeId, '?context=apps');
    window.open(url, '_blank');
  };

  const goToIntegrationPage = () => {
    const url = getFullAppUrlV2(routes.shopifyIntegration.view());
    window.open(url, '_blank');
  };

  const handleCloseWarning = () => {
    try {
      localStorage.setItem(`${getShop()}-closeWarningThemeTurboInPredictiveBundleBanner`, true);
      setWarning(false);
    } catch (err) {}
  };

  useEffect(() => {
    try {
      const data = localStorage.getItem(
        `${getShop()}-closeWarningThemeTurboInPredictiveBundleBanner`
      );
      if (data) {
        setWarning(false);
      }
    } catch (err) {}
  }, []);

  return (
    <div className="BC-Page PFS-PredictiveBundleItem_Placement">
      <FullscreenBar
        onAction={handleCloseFullscreenPage}
        // backTitle={i18n.translate('AdminHTML.Common.Action.exit')}
        title={i18n.translate(`AdminHTML.PredictiveBundle.Item.lastStepTitle`)}
        actions={
          <ButtonGroup>
            <Button primary onClick={handleCompleteSetup} loading={saving}>
              {i18n.translate('AdminHTML.PredictiveBundle.Item.complete')}
            </Button>
          </ButtonGroup>
        }
        progress={100}
      />
      <div style={{ marginTop: '2rem' }}>
        <Page>
          <Layout>
            <Layout.Section>
              <Card sectioned>
                {showWarning && (
                  <Banner onDismiss={() => handleCloseWarning()} status="warning">
                    <p>
                      {i18n.translate(
                        `AdminHTML.PredictiveBundle.Item.${
                          oldStore ? 'placement_WarningTheme02' : 'placement_WarningTheme01'
                        }`,
                        {
                          version: (
                            <TextStyle variation="strong">
                              {i18n.translate(
                                'AdminHTML.PredictiveBundle.Item.placement_WarningTheme_version'
                              )}
                            </TextStyle>
                          ),
                          t: (
                            <Link external onClick={goToIntegrationPage}>
                              {i18n.translate(
                                'AdminHTML.PredictiveBundle.Item.placement_WarningTheme_t'
                              )}
                              <Icon source={ExternalMinor} color="interactive" />
                            </Link>
                          ),
                          turbo: (
                            <TextStyle variation="strong">
                              {i18n.translate(
                                'AdminHTML.PredictiveBundle.Item.placement_WarningTheme02_t'
                              )}
                            </TextStyle>
                          )
                        }
                      )}
                    </p>
                  </Banner>
                )}
                <div className="setup-content">
                  <div className="tab-content">
                    <div className="tab-content-content">
                      <Grid>
                        <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                          <div>
                            <TextStyle variation="strong">
                              {i18n.translate(
                                `AdminHTML.PredictiveBundle.Item.${
                                  placement.includes('instant-search-widget')
                                    ? 'placement_Step1_Title'
                                    : 'placement_Step1_Title01'
                                }`
                              )}
                            </TextStyle>
                            <span style={{ marginLeft: '0.375rem' }}>
                              <Badge status="attention">
                                {i18n.translate('AdminHTML.PredictiveBundle.Item.require')}
                              </Badge>
                            </span>
                          </div>
                          <p>
                            {i18n.translate(
                              'AdminHTML.PredictiveBundle.Item.placement_Step1_Content',
                              {
                                t1: (
                                  <TextStyle variation="strong">
                                    {i18n.translate(
                                      'AdminHTML.PredictiveBundle.Item.placement_Step1_Content_t1'
                                    )}
                                  </TextStyle>
                                )
                              }
                            )}
                          </p>
                          <div className="action">
                            {checkingEnableBoostCore ? (
                              <SkeletonDisplayText />
                            ) : isEnableBoostCore ? (
                              <div className="non-action">
                                <Icon source={CircleTickMajor} color="primary" />
                                <TextStyle variation="positive">
                                  {i18n.translate(
                                    'AdminHTML.PredictiveBundle.Item.placement_Step1_DisabledAction'
                                  )}
                                </TextStyle>
                              </div>
                            ) : (
                              <>
                                <Button primary onClick={enableBoostScore}>
                                  {i18n.translate(
                                    'AdminHTML.PredictiveBundle.Item.placement_Step1_Action'
                                  )}
                                </Button>
                                <div className="content">
                                  {i18n.translate(
                                    'AdminHTML.PredictiveBundle.Item.placement_Step1_Content02',
                                    {
                                      t1: (
                                        <TextStyle variation="strong">
                                          {i18n.translate(
                                            'AdminHTML.PredictiveBundle.Item.placement_Step1_Content02_t1'
                                          )}
                                        </TextStyle>
                                      )
                                    }
                                  )}
                                  <ul style={{ marginTop: 0, paddingLeft: '1.25rem' }}>
                                    <li>
                                      {i18n.translate(
                                        'AdminHTML.PredictiveBundle.Item.placement_Step1_Content02_Line01',
                                        {
                                          t1: (
                                            <TextStyle variation="strong">
                                              {i18n.translate(
                                                'AdminHTML.PredictiveBundle.Item.placement_Step1_Content02_Line01_t1'
                                              )}
                                            </TextStyle>
                                          ),
                                          t2: (
                                            <TextStyle variation="strong">
                                              {i18n.translate(
                                                'AdminHTML.PredictiveBundle.Item.placement_Step1_Content02_Line01_t2'
                                              )}
                                            </TextStyle>
                                          ),
                                          t3: (
                                            <Link onClick={goToThemeEditor}>
                                              {i18n.translate(
                                                'AdminHTML.PredictiveBundle.Item.placement_Step1_Content02_Line01_t3'
                                              )}
                                            </Link>
                                          )
                                        }
                                      )}
                                    </li>
                                    <li>
                                      {i18n.translate(
                                        'AdminHTML.PredictiveBundle.Item.placement_Step1_Content02_Line02',
                                        {
                                          t1: (
                                            <TextStyle variation="strong">
                                              {i18n.translate(
                                                'AdminHTML.PredictiveBundle.Item.placement_Step1_Content02_Line02_t1'
                                              )}
                                            </TextStyle>
                                          )
                                        }
                                      )}
                                    </li>
                                    <li>
                                      {i18n.translate(
                                        'AdminHTML.PredictiveBundle.Item.placement_Step1_Content02_Line03'
                                      )}
                                    </li>
                                  </ul>
                                  {templateId && !_.isEmpty(templateId) && (
                                    <div className="html-placeholder">
                                      <TextField
                                        name="templateId"
                                        id="templateId"
                                        value={templateId}
                                        disabled="disabled"
                                        suffix={
                                          <Button
                                            icon={DuplicateMinor}
                                            plain
                                            onClick={() => copyClipBoard(templateId)}
                                          />
                                        }
                                      />
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </Grid.Cell>
                        <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                          <div className="BC-Iframe-Container">
                            <img
                              className="clickable"
                              src={EnableBoostCore}
                              alt="enable-boost-core"
                              style={{ width: '100%' }}
                              onClick={() =>
                                window.open(EnableBoostCore, '_blank', 'noopener,noreferrer')
                              }
                            />
                          </div>
                        </Grid.Cell>
                      </Grid>
                      {placement.includes('instant-search-widget') &&
                        !_.isEmpty(instantSearchBlockDeepLink) && (
                          <Grid>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                              <TextStyle variation="strong">
                                {i18n.translate(
                                  'AdminHTML.PredictiveBundle.Item.placement_Step2_Title'
                                )}
                              </TextStyle>
                              <p>
                                {i18n.translate(
                                  'AdminHTML.PredictiveBundle.Item.placement_Step2_Content01'
                                )}
                              </p>
                              {checkingEnableBoostCore ? (
                                <SkeletonDisplayText />
                              ) : isEnableISWBlock ? (
                                <div className="action">
                                  <div className="non-action">
                                    <Icon source={CircleTickMajor} color="primary" />
                                    <TextStyle variation="positive">
                                      {i18n.translate(
                                        'AdminHTML.PredictiveBundle.Item.placement_Step2_DisabledAction'
                                      )}
                                    </TextStyle>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <ul
                                    className="content"
                                    style={{ marginTop: 24, paddingLeft: '1.25rem' }}>
                                    <li>
                                      {i18n.translate(
                                        'AdminHTML.PredictiveBundle.Item.placement_Step2_Content02',
                                        {
                                          t: (
                                            <TextStyle variation="strong">
                                              {i18n.translate(
                                                'AdminHTML.PredictiveBundle.Item.placement_Step2_Content02_t'
                                              )}
                                            </TextStyle>
                                          )
                                        }
                                      )}
                                    </li>
                                  </ul>
                                  <div className="action">
                                    <Button primary onClick={enableInstantSearchBlock}>
                                      {i18n.translate(
                                        'AdminHTML.PredictiveBundle.Item.placement_Step2_Action'
                                      )}
                                    </Button>
                                  </div>
                                </>
                              )}
                            </Grid.Cell>
                            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                              <div className="BC-Iframe-Container">
                                <img
                                  className="clickable"
                                  src={EnableISW}
                                  alt="enable-boost-core"
                                  style={{ width: '100%' }}
                                  onClick={() =>
                                    window.open(EnableISW, '_blank', 'noopener,noreferrer')
                                  }
                                />
                              </div>
                            </Grid.Cell>
                          </Grid>
                        )}
                    </div>
                  </div>
                </div>
              </Card>
            </Layout.Section>
          </Layout>
        </Page>
      </div>
    </div>
  );
};
const mapDispatchToProps = (dispatch, ownProps) => ({
  // Check enable boost core on live theme
  // checkEnableBoostCore: queries =>
  //   dispatch(recommendationAction.checkEnableBoostCore(queries, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const enableAppVersionSwitching = checkUserEnableShowAppVersionSwitching(tenantApp);
  const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);
  return {
    enableAppVersionSwitching,
    isEnableShopifyIntegration,
    saving: _.get(state.recommendation, 'saving', false),
    checkingEnableBoostCore: _.get(state.recommendation, 'checkingEnableBoostCore', false),
    isEnableBoostCore: _.get(state.recommendation, 'isEnableBoostCore', false),
    isEnableISWBlock: _.get(state.recommendation, 'isEnableISWBlock', false),
    instantSearchBlockDeepLink: _.get(state.bundle, 'instantSearchBlockDeepLink', ''),
    embedDeepLink: _.get(state.bundle, 'embedDeepLink', ''),
    widgetId: _.get(state.bundle.widget, 'widgetId', ''),
    widget: _.get(state.bundle, 'widget', {}),
    templateId: _.get(state.themeIntegration, 'templateInstance.templateId', ''),
    themeName: _.get(state.themeSetting, 'themeName', '')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Placement));
